import React, { useEffect, useMemo, useRef, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { SurveyModel, TSurveyModel } from "@performyard/models";
import { ButtonStandard } from "@performyard/py-components";

import OptionsDrawer from "../../../../../components/OptionsDrawer";
import PageHeading from "../../../../../components/PageHeading";
import Pagination from "../../../../../components/Pagination";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import { getSurveyNameCell } from "./CellRenderers";
import { SurveyFilterPanel, SurveyFilters } from "./SurveyFilterPanel";
import SurveysModals from "./SurveysModals";

import { SURVEY_MODALS } from "../store/model";
import { useSetters } from "../store/store";

import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { useInternalApiSearchService } from "../../../../../api/hooks/defs/search-service";

import { getFiltersForSearchService } from "../utils";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { SURVEYS_TABLE_LIMIT, surveysConfig } from "../config";

const SURVEYS_VIEW_HEADINGS: Record<string, string> = {
	"all-surveys": "All Surveys",
	"my-surveys": "My Surveys",
};

const OPTIONS_COLUMN_WIDTH = 40;
const MIN_NAME_COLUMN_WIDTH = 280;

type Props = {
	navLinks: { key: string; label: string; href: string }[];
	activeNavigationLinkKey: string;
	showCreateButton?: boolean;
	showDeleteOption?: boolean;
	showFilter: boolean;
	extraFilters?: Record<string, unknown>;
	disabled?: boolean;
	refetchOnMount?: boolean;
};

const SurveysTabLayout = ({
	navLinks,
	activeNavigationLinkKey,
	showCreateButton = false,
	showDeleteOption = false,
	showFilter,
	extraFilters,
	disabled,
	refetchOnMount = false,
}: Props) => {
	const setters = useSetters();
	const isNewNav = useIsNewNavigation();

	// Fill the single-column side-nav content area: size the Survey Name column to
	// consume whatever the fixed columns + ScrollableTable padding leave, so the
	// options column sits flush at the right edge instead of leaving an empty gap.
	const tableWrapperRef = useRef<HTMLDivElement>(null);
	const [nameColumnWidth, setNameColumnWidth] = useState(400);
	useEffect(() => {
		const element = tableWrapperRef.current;
		if (!element) return;
		const fixedColumnsWidth =
			surveysConfig.allSurveysStaticTableCols
				.filter((col) => col.id !== "name")
				.reduce((total, col) => total + (col.width ?? 0), 0) + OPTIONS_COLUMN_WIDTH;
		const columnCount = surveysConfig.allSurveysStaticTableCols.length + 1;
		// ScrollableTable adds tablePadding (8) per column plus an edge tablePadding and horizontalPadding (16).
		const tablePadding = columnCount * 8 + 8 + 16;
		const update = () => {
			setNameColumnWidth(Math.max(element.clientWidth - fixedColumnsWidth - tablePadding, MIN_NAME_COLUMN_WIDTH));
		};
		update();
		const observer = new ResizeObserver(update);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		searchValue,
		setDebouncedSearch,
		debouncedSearchValue,
		filters,
		setFilters,
	} = useInfoPageTemplateQueryState<SurveyFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const searchFilters = useMemo(
		() => ({
			...getFiltersForSearchService(filters, debouncedSearchValue),
			...extraFilters,
		}),
		[filters, debouncedSearchValue, extraFilters]
	);

	const {
		data: surveys,
		isLoading,
		refetch,
	} = useInternalApiSearchService({
		service: "survey",
		filters: searchFilters,
		skip: (pageNumber - 1) * SURVEYS_TABLE_LIMIT,
		limit: SURVEYS_TABLE_LIMIT,
		sort: { name: sort.name, direction: sort.direction },
		disabled,
	});

	const parsedSurveysResults = useMemo(() => {
		return (surveys?.results || []).map((survey) => SurveyModel.parse(survey));
	}, [surveys]);

	useEffect(() => {
		if (refetchOnMount && !isLoading) {
			refetch();
		}
	}, []);

	const handleOptionSelection = (option: { id: string; label: string }, survey: TSurveyModel) => {
		setters.setSurveyId(survey._id.toString());
		setters.setSurveyLink(survey.surveyLink ? `${window.location.origin}${survey.surveyLink}` : "");
		setters.setSurveyName(survey.name);
		if (option.id === "copy-link") {
			setters.setModal(SURVEY_MODALS.SURVEY_LINK);
		} else if (option.id === "copy") {
			setters.setModal(SURVEY_MODALS.COPY_SURVEY);
		} else if (option.id === "close") {
			setters.setModal(SURVEY_MODALS.CLOSE_SURVEY);
		} else if (option.id === "delete") {
			setters.setModal(SURVEY_MODALS.DELETE_SURVEY);
		}
	};

	const columns = [
		...surveysConfig.allSurveysStaticTableCols.map((col) =>
			col.id === "name"
				? {
						...col,
						width: nameColumnWidth,
						getter: (survey: TSurveyModel) => getSurveyNameCell(survey, activeNavigationLinkKey),
					}
				: col
		),
		{
			id: "options",
			label: "",
			width: OPTIONS_COLUMN_WIDTH,
			isSortable: false,
			getter: (survey: TSurveyModel) => {
				const { status } = survey;
				const options = [
					...(["active", "scheduled"].includes(status)
						? [{ id: "copy-link", label: "Copy Survey Link" }]
						: []),
					{ id: "copy", label: "Save as Copy" },
					...(["active", "scheduled"].includes(status) ? [{ id: "close", label: "Close Survey" }] : []),
					...(showDeleteOption ? [{ id: "delete", label: "Delete Survey" }] : []),
				];
				return (
					<div className="flex justify-end">
						<OptionsDrawer
							horizontal
							optionWidth={110}
							options={options}
							openOn="bottom-end"
							onSelect={(option) => handleOptionSelection(option, survey)}
						/>
					</div>
				);
			},
		},
	];

	return (
		<>
			<InfoPageTemplate
				navigationLinks={navLinks}
				pageHeading={
					isNewNav ? (
						<PageHeading>{SURVEYS_VIEW_HEADINGS[activeNavigationLinkKey] ?? "Surveys"}</PageHeading>
					) : undefined
				}
				actionButtons={
					showCreateButton
						? [
								<ButtonStandard
									key="create-survey"
									color="green"
									iconLeft={faPlus}
									onClick={() => setters.setModal("create-survey")}
								>
									Create Survey
								</ButtonStandard>,
							]
						: []
				}
				toolbarActionRow={
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={setDebouncedSearch}
						searchPlaceholder="Search surveys..."
						onFilterButtonPress={showFilter ? () => setIsFiltersPanelOpen(true) : undefined}
						activeFiltersCount={showFilter ? Object.keys(filters).length : undefined}
					/>
				}
				activeNavigationLinkKey={activeNavigationLinkKey}
				mainContent={
					<div ref={tableWrapperRef} data-testid="list-surveys-table">
						<ScrollableTable
							columns={columns}
							rows={parsedSurveysResults}
							isLoading={isLoading}
							activeSort={sort.name}
							activeSortDirection={sort.direction}
							onColumnClick={(column: string) =>
								setSort({ name: column, direction: sort.direction === "asc" ? "desc" : "asc" })
							}
							emptyChildren={<EmptySearchTableMessage mainText="No Surveys Found" />}
						/>
						<Spacer height={3} />
						<Pagination
							pageCount={Math.ceil((surveys?.total || SURVEYS_TABLE_LIMIT) / SURVEYS_TABLE_LIMIT)}
							currentPage={pageNumber}
							onClick={(page: number) => setPageNumber(page)}
						/>
						<Spacer height={2} />
					</div>
				}
				pageTitle="Surveys"
				drawer={
					showFilter ? (
						<SurveyFilterPanel
							isOpen={isFiltersPanelOpen}
							onClose={() => setIsFiltersPanelOpen(false)}
							filters={filters}
							onApplyFilters={setFilters}
						/>
					) : undefined
				}
			/>
			<SurveysModals refetch={refetch} />
		</>
	);
};

export default SurveysTabLayout;
